// Shared with my-utr-selfrate and utr-web-app (ENG-2782 / ENG-3097): the names, values and lifetimes are
// a cross-app contract on the .utrsports.net parent domain. Changing one here breaks the hand-off.
export const SIGNIN_COOKIE_NAME = 'utr_evp_signin';
export const SIGNIN_PENDING_VALUE = 'pending';
export const DISMISSED_COOKIE_PREFIX = 'utr_evp_dismissed_';
export const DISMISSED_COOKIE_VALUE = '1';
export const DISMISSED_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7;

const ROOT_DOMAIN = 'utrsports.net';
const HTTPS_PROTOCOL = 'https:';

export const buildDismissedCookieName = (memberId) => `${DISMISSED_COOKIE_PREFIX}${memberId}`;

// Hosts under utrsports.net share the cookie with the other UTR apps; anything else (localhost, LAN IPs)
// gets a host-only cookie, because a Domain the browser does not own drops the cookie entirely.
export const resolveCookieDomain = (hostname) => {
    if (!hostname) {
        return null;
    }

    if (hostname === ROOT_DOMAIN || hostname.endsWith(`.${ROOT_DOMAIN}`)) {
        return `.${ROOT_DOMAIN}`;
    }

    return null;
};

// `Secure` follows the page protocol: a Secure cookie written from http localhost is silently dropped.
export const buildPromptCookie = (name, value, maxAgeSeconds, { hostname, protocol } = {}) => {
    const domain = resolveCookieDomain(hostname);
    const segments = [
        `${name}=${value}`,
        domain ? `Domain=${domain}` : null,
        'Path=/',
        `Max-Age=${maxAgeSeconds}`,
        'SameSite=Lax',
        protocol === HTTPS_PROTOCOL ? 'Secure' : null,
    ];

    return segments.filter(Boolean).join('; ');
};

const currentPageLocation = () => ({
    hostname: window.location?.hostname,
    protocol: window.location?.protocol,
});

// Blocked or sandboxed storage can make document.cookie throw; a failed write is dropped silently,
// because the prompt is a nudge and must never break the page.
const writeCookie = (name, value, maxAgeSeconds) => {
    try {
        document.cookie = buildPromptCookie(name, value, maxAgeSeconds, currentPageLocation());
        return true;
    } catch {
        // Ignored on purpose: blocked cookies must not break the page, so the write is reported as not done.
        return false;
    }
};

const readCookieFrom = (cookieString, name) => {
    const prefix = `${name}=`;
    const match = (cookieString || '')
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix));

    if (!match) {
        return null;
    }

    try {
        return decodeURIComponent(match.slice(prefix.length));
    } catch {
        // Ignored on purpose: a malformed escape sequence is kept as the raw value rather than failing the read.
        return match.slice(prefix.length);
    }
};

const isPresentMemberId = (memberId) => memberId !== null && memberId !== undefined && memberId !== '';

export const clearSignedIn = () => writeCookie(SIGNIN_COOKIE_NAME, '', 0);

export const markDismissed = (memberId) => {
    if (!isPresentMemberId(memberId)) {
        return false;
    }

    return writeCookie(buildDismissedCookieName(memberId), DISMISSED_COOKIE_VALUE, DISMISSED_COOKIE_MAX_AGE_SECONDS);
};

// `readable: false` tells the watcher the cookies cannot be trusted either way, so it falls back to
// showing the prompt at most once per page load.
export const readPromptCookies = (memberId) => {
    try {
        const cookieString = document.cookie;

        return {
            readable: true,
            signIn: readCookieFrom(cookieString, SIGNIN_COOKIE_NAME),
            dismissed: readCookieFrom(cookieString, buildDismissedCookieName(memberId)) !== null,
        };
    } catch {
        // Ignored on purpose: blocked cookies must not break the page; `readable: false` hands the
        // decision to the watcher's once-per-page-load fallback.
        return { readable: false, signIn: null, dismissed: false };
    }
};

// `pending` is written by OAuth sign-in buttons that do not know the member yet, so it matches whoever
// is signed in. A value naming a different member is ignored and left to expire on its own.
export const signInCookieMatchesMember = (signInValue, memberId) => {
    if (!signInValue || !isPresentMemberId(memberId)) {
        return false;
    }

    return signInValue === SIGNIN_PENDING_VALUE || signInValue === String(memberId);
};
